import {useCallback} from 'react';

import {FeatureDefinition} from 'auth/feature-enablement/FeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import useStepTypeInfos from 'pipelines/hooks/useStepTypeInfosQuery';
import {getPipelineType} from 'pipelines/PipelineTypes';

type BenchlingConfigKey = keyof typeof BenchlingConfig;

function isConfigFlagEnabled(flag: string): boolean {
  return Boolean(BenchlingConfig[flag as BenchlingConfigKey]);
}

function isCapabilityManagementFeatureEnabled(feature: string): boolean {
  return isFeatureEnabled({feature} as FeatureDefinition);
}

/**
 * Check whether a step type is enabled for the current user to create and run.
 *
 * @param forCreate - Set true to check if the user can create a step; set false to check if the
 *   user can update or run a step.
 */
export type CheckStepTypeEnabledForUser = (stepType: PipelineStepType, forCreate?: boolean) => boolean;

/**
 * Builds a function to check whether a step type is enabled for the current user.
 *
 * The gating derives from these fields of the step type's YAML manifest:
 * - `configFlags`
 * - `capabilityManagementFeatures`
 * - `allowedPipelineTypes` / `blockedPipelineTypes`
 * - `userCanCreateDirectly` (if `forCreate` is `true`)
 *
 * `configFlags`, `capabilityManagementFeatures`, and the pipeline-type gating gate the ability to
 * create or run a step. `userCanCreateDirectly` gates the ability to directly create a new step,
 * but allows updating and running existing steps, and creating steps indirectly (e.g. by applying
 * a static template).
 *
 * The returned check reports every step type as disabled while the step type infos are loading.
 */
export default function useCheckStepTypeEnabledForUser(pipelineId: string): CheckStepTypeEnabledForUser {
  const pipelineType = getPipelineType(pipelineId);
  const stepTypeInfos = useStepTypeInfos();

  return useCallback(
    (stepType: PipelineStepType, forCreate: boolean = true): boolean => {
      const info = stepTypeInfos.get(stepType);
      if (info === undefined) {
        return false;
      }
      if (!info.configFlags.every((flag) => isConfigFlagEnabled(flag))) {
        return false;
      }
      if (
        !info.requiredCapabilityManagementFeatures.every((feature) =>
          isCapabilityManagementFeatureEnabled(feature)
        )
      ) {
        return false;
      }
      if (forCreate && !info.userCanCreateDirectly) {
        return false;
      }
      return !info.blockedPipelineTypes.includes(pipelineType);
    },
    [pipelineType, stepTypeInfos]
  );
}
